import {
  BookingParams, BookingPosition,
  ConstraintLevel,
  PositionContextSummary,
} from '../types';
import { ConstraintRule } from './constraint-rule';

/**
 * Sammelt Validierungsregeln und prüft eine Position sequentiell gegen alle
 * registrierten Regeln, sortiert das Ergebnis nach blockierend und Warnung.
 */
export class ConstraintChecker {
  private readonly rules: ConstraintRule[];

  constructor() {
    this.rules = [];
  }

  /**
   * Registriert Regelklassen als blockierende Regeln.
   * @param blockers Regel-Konstruktoren, die mit Level BLOCKING instanziiert werden.
   */
  public registerAsBlocking<T extends ConstraintRule>(
    ...blockers: (new (level: ConstraintLevel) => T)[]
  ): void {
    for (const blocker of blockers) {
      this.rules.push(new blocker(ConstraintLevel.BLOCKING));
    }
  }

  /**
   * Registriert Regelklassen als warnende Regeln.
   * @param warnings Regel-Konstruktoren, die mit Level WARNING instanziiert werden.
   */
  public registerAsWarning<T extends ConstraintRule>(
    ...warnings: (new (level: ConstraintLevel) => T)[]
  ): void {
    for (const warning of warnings) {
      this.rules.push(new warning(ConstraintLevel.WARNING));
    }
  }

  /**
   * Prüft eine Position sequentiell gegen alle registrierten Regeln.
   * @param position Zu prüfende Buchungsposition.
   * @param params Buchungsparameter mit Kontext und Adaptern.
   * @returns Zusammenfassung mit den nicht erfüllten Regeln, getrennt nach blockierend und Warnung.
   */
  public async validate(
    position: BookingPosition,
    params: BookingParams,
  ): Promise<PositionContextSummary> {
    const result: PositionContextSummary = {
      position,
      context: params.context,
      blocking: [],
      warnings: [],
    };
    for (const rule of this.rules) {
      const needsCheck = await rule.needsCheck(position, params);
      if (!needsCheck) continue;
      const summary = await rule.check(position, params);
      if (summary.satisfied) continue;
      switch (summary.level) {
        case ConstraintLevel.BLOCKING:
          result.blocking.push(summary);
          break;
        case ConstraintLevel.WARNING:
          result.warnings.push(summary);
          break;
      }
    }
    return result;
  }
}
